import React from "react";
import { Layout } from "../components/Layout";
import { useLanguage } from "../context/LanguageContext";
import { siteContent } from "../mock/rosablu.mock";
import { Card } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";

export default function ActivitiesPage() {
  const { lang } = useLanguage();
  const data = siteContent.activities[lang];
  const sections = data.sections;

  const galleryImages = [
    {
      src: "/images/rosablu/gallery/montagne-neve.webp",
      altIt: "Pareti rocciose innevate in Val di Sole",
      altEn: "Snowy rocky peaks in Val di Sole",
    },
    {
      src: "/images/rosablu/gallery/piste-sci.webp",
      altIt: "Piste da sci in Val di Sole",
      altEn: "Ski slopes in Val di Sole",
    },
    {
      src: "/images/rosablu/gallery/commezzadura-estate.webp",
      altIt: "Commezzadura in estate",
      altEn: "Commezzadura in summer",
    },
    {
      src: "/images/rosablu/gallery/lago-valdisole.webp",
      altIt: "Lago alpino in Val di Sole",
      altEn: "Alpine lake in Val di Sole",
    },
    {
      src: "/images/rosablu/gallery/mestriago-inverno.webp",
      altIt: "Borgo di Mestriago in inverno",
      altEn: "Mestriago village in winter",
    },
  ];

  return (
    <Layout>
      {/* HERO ATTIVITÀ (NON LAZY) */}
      <section className="relative overflow-hidden bg-slate-900 text-slate-50">
        <div className="absolute inset-0">
          <img
            src="/images/rosablu/gallery/casa-montagne.webp"
            alt={
              lang === "it"
                ? "Case e montagne a Commezzadura"
                : "Houses and mountains in Commezzadura"
            }
            width={1600}
            height={900}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
        </div>
        <div className="relative mx-auto flex max-w-4xl flex-col items-center px-4 pb-18 pt-18 text-center md:pb-20 md:pt-20">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-slate-200">
            {lang === "it" ? "Attività" : "Activities"}
          </p>
          <h1 className="mb-3 font-serif text-3xl leading-tight md:text-4xl">
            {data.title}
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-slate-100/90 md:text-base">
            {data.intro}
          </p>
        </div>
      </section>

      {/* CAROSELLO FOTOGRAFICO VAL DI SOLE (LAZY) */}
      <section className="bg-white pattern-activities py-10 md:py-12">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <Carousel className="w-full">
            <CarouselContent>
              {galleryImages.map((img, index) => (
                <CarouselItem
                  key={index}
                  className="basis-full sm:basis-1/2 lg:basis-1/3"
                >
                  <div className="overflow-hidden rounded-3xl">
                    <img
                      loading="lazy"
                      src={img.src}
                      alt={lang === "it" ? img.altIt : img.altEn}
                      className="aspect-[4/3] w-full cursor-pointer object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        </div>
      </section>

      {/* MACRO SEZIONI + IMMAGINI (CARD LAZY) */}
      <section className="bg-white pattern-activities pb-14 pt-2 md:pb-18">
        <div className="mx-auto max-w-6xl space-y-8 px-4 md:px-6">
          {/* Inverno */}
          <div className="grid gap-6 md:grid-cols-[1.4fr,1fr] md:items-center">
            <div>
              <h2 className="font-serif text-2xl text-slate-900 md:text-3xl">
                {sections.winter.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-700 md:text-base">
                {sections.winter.subtitle}
              </p>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-700">
                {sections.winter.items.slice(0, 4).map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-[#b07157]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="overflow-hidden rounded-3xl bg-[#f8f1e8] p-4 text-sm text-slate-800 shadow-sm">
              <img
                loading="lazy"
                src="/images/rosablu/gallery/sciatore-neve.webp"
                alt={
                  lang === "it"
                    ? "Sciatore fuoripista in Val di Sole"
                    : "Off-piste skier in Val di Sole"
                }
                className="mb-4 aspect-[4/3] w-full rounded-2xl object-cover"
              />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                {lang === "it" ? "Inverno" : "Winter"}
              </p>
            </Card>
          </div>

          {/* Estate */}
          <div className="grid gap-6 md:grid-cols-[1fr,1.4fr] md:items-center">
            <Card className="order-2 overflow-hidden rounded-3xl bg-[#f6f0ea] p-4 text-sm text-slate-800 shadow-sm md:order-1">
              <img
                loading="lazy"
                src="/images/rosablu/gallery/prati-estate.webp"
                alt={
                  lang === "it"
                    ? "Prati estivi in Val di Sole"
                    : "Summer meadows in Val di Sole"
                }
                className="mb-4 aspect-[4/3] w-full rounded-2xl object-cover"
              />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                {lang === "it" ? "Estate" : "Summer"}
              </p>
            </Card>
            <div className="order-1 md:order-2">
              <h2 className="font-serif text-2xl text-slate-900 md:text-3xl">
                {sections.summer.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-700 md:text-base">
                {sections.summer.subtitle}
              </p>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-700">
                {sections.summer.items.slice(0, 4).map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-[#b07157]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Dintorni */}
          <div className="grid gap-6 md:grid-cols-[1.4fr,1fr] md:items-center">
            <div>
              <h2 className="font-serif text-2xl text-slate-900 md:text-3xl">
                {sections.surroundings.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-700 md:text-base">
                {sections.surroundings.subtitle}
              </p>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-700">
                {sections.surroundings.items.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-[#b07157]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="overflow-hidden rounded-3xl bg-[#f8f1e8] p-4 text-sm text-slate-800 shadow-sm">
              <img
                loading="lazy"
                src="/images/rosablu/gallery/trentino-panorama.webp"
                alt={
                  lang === "it"
                    ? "Panorama del Trentino"
                    : "Trentino landscape"
                }
                className="mb-4 aspect-[4/3] w-full rounded-2xl object-cover"
              />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                {lang === "it" ? "Dintorni" : "Surroundings"}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* DETTAGLIO IN ACCORDION (solo testo, nessuna immagine da toccare) */}
      <section className="bg-[#f6f0ea] py-14 md:py-18">
        <div className="mx-auto max-w-5xl px-4 md:px-6">
          <h2 className="font-serif text-2xl text-slate-900 md:text-3xl">
            {lang === "it" ? "Dettaglio attività" : "Activities in detail"}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-700 md:text-base">
            {lang === "it"
              ? "Apri le sezioni per scoprire più nel dettaglio cosa puoi vivere durante il tuo soggiorno a Rosa Blu Apartment."
              : "Open the sections to explore in detail what you can experience during your stay at Rosa Blu Apartment."}
          </p>
          <div className="mt-6 rounded-3xl border border-slate-100 bg-white/80 p-4 shadow-sm backdrop-blur-md md:p-6">
            <Accordion type="single" collapsible className="w-full">
              {Object.entries(sections).map(([key, section]) => (
                <AccordionItem
                  key={key}
                  value={key}
                  className="border-b border-slate-100 last:border-0"
                >
                  <AccordionTrigger className="text-left text-sm font-medium text-slate-800">
                    {section.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="mb-3 text-xs leading-relaxed text-slate-700">
                      {section.subtitle}
                    </p>
                    <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
                      {section.items.map((item) => (
                        <li key={item} className="flex gap-2">
                          <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-[#b07157]" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </Layout>
  );
}
